import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { assetSchema, type AssetInput, assetStatuses } from '../../../shared/types'
import { apiRequest, twinApi, assetsApi } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { TwinStatusBadge } from '../TwinViewer'

interface AssetFormProps {
  defaultValues?: Partial<AssetInput> & { twinProjectId?: string | null }
  onSubmit: (data: AssetInput) => void
  onCancel: () => void
  loading?: boolean
  assetId?: string // For edit mode - to exclude current asset's twin from unavailable list
}

export function AssetForm({ defaultValues, onSubmit, onCancel, loading, assetId }: AssetFormProps) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AssetInput>({
    resolver: zodResolver(assetSchema),
    defaultValues: {
      status: 'active',
      ...defaultValues,
    },
  })

  // Fetch accounts for dropdown
  const { data: accountsResponse } = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => apiRequest<{ data: { id: string; name: string }[] }>('/accounts?limit=100'),
  })

  const accounts = accountsResponse?.data || []

  // Fetch available Twin projects for linking (only unlinked projects)
  const { data: availableTwinProjects } = useQuery({
    queryKey: ['twin-projects', 'available-for-form', assetId],
    queryFn: async () => {
      const allProjects = await twinApi.listProjects()
      // Get all assets to find which have twin projects linked
      const assetsResponse = await assetsApi.list({ limit: 1000 })
      const linkedTwinIds = new Set(
        assetsResponse.data
          ?.filter(a => a.twinProjectId && a.id !== assetId)
          .map(a => a.twinProjectId) || []
      )
      // Return only projects that are not linked to other assets
      // Include the current asset's twin project if editing
      return allProjects.filter(p => !linkedTwinIds.has(p.id))
    },
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Serial Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('pages.assets.serialNumber')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('serialNumber')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder={t('pages.assets.serialNumberPlaceholder')}
        />
        {errors.serialNumber && (
          <p className="mt-1 text-sm text-red-600">{errors.serialNumber.message}</p>
        )}
      </div>

      {/* Account */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('fields.account')} <span className="text-red-500">*</span>
        </label>
        <select
          {...register('accountId')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          <option value="">{t('pages.assets.selectAccount')}</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>
        {errors.accountId && (
          <p className="mt-1 text-sm text-red-600">{errors.accountId.message}</p>
        )}
      </div>

      {/* Product Line */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('pages.assets.productLine')}</label>
        <input
          type="text"
          {...register('productLine')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder={t('pages.assets.productLinePlaceholder')}
        />
      </div>

      {/* Model */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('pages.assets.model')}</label>
        <input
          type="text"
          {...register('model')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder={t('pages.assets.modelPlaceholder')}
        />
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('fields.status')}</label>
        <select
          {...register('status')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          {assetStatuses.map((status) => (
            <option key={status} value={status}>
              {t(`status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Commissioning Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('pages.assets.commissioningDate')}</label>
        <input
          type="datetime-local"
          {...register('commissioningDate')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
      </div>

      {/* Warranty Expiry */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('pages.assets.warrantyExpiry')}</label>
        <input
          type="datetime-local"
          {...register('warrantyExpiry')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('pages.assets.notes')}</label>
        <textarea
          {...register('notes')}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          placeholder={t('pages.assets.notesPlaceholder')}
        />
      </div>

      {/* Twin Project */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('twin.title')}{' '}
          <span className="text-gray-400 font-normal">({t('common.optional')})</span>
        </label>
        <select
          {...register('twinProjectId')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          <option value="">{t('twin.upload.noAsset')}</option>
          {availableTwinProjects?.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
              {project.status !== 'ready' ? ` (${t(`twin.status.${project.status}`)})` : ''}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {t('twin.upload.description')}
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
